'use client'

import * as React from 'react'
import { parseTab, useTabsStore } from '../explore/components/tabs/store'
import { DocumentBlock } from '../explore/components/blocks/document'
import { WorkingBlockDiff } from '../changes/components/diff'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { Timeline } from '../branch/_components/timeline'
import { BreadcrumbDashboard } from '../explore/components/tabs/dashboard-breadcrumb'
import { CommitContent } from '../history/_components/content'
import { BranchChangesTabContent } from '../branch/_components/changes'
import { ChangelogEditorEntry } from './changelog'
import { BranchManager } from './branch-manager'
import { DevTabContent } from '../dev/tab-content'
import { SettingsTabContent } from '../settings/tab-content'
import {
  useIsExternalVisitor,
  useIsGuest,
  useIsGuestInHisBranch,
} from '~/hooks-data/use-user-role'
import { useSelectedBranch } from '~/lib/commits/use-selected-branch-or-commit'
import { SiteTabContent } from '../start/tab-content'
import { SuggestionContent } from './suggestion/content'
import { AgentsTabContent } from '../agents/tab-content'

const TabWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-col flex-1 w-full bg-dark-base">{children}</div>
  )
}

const READMETabContent = () => (
  <SettingsTabContent
    parsedTab={{
      key: 'settings:public-info',
      label: 'README',
      type: 'settings',
      partiallyOpen: false,
      tab: 'public-info',
    }}
  />
)

export const TabContent = ({
  onboardingGuides,
}: {
  onboardingGuides?: React.ReactNode
}) => {
  const activeTab = useTabsStore((s) => s.activeTab)
  const hasRendered = useHasRendered()
  const parsedActiveTab = activeTab ? parseTab(activeTab) : null

  if (!hasRendered) return null
  if (!parsedActiveTab) {
    return (
      <TabWrapper>
        <DefaultTab onboardingGuides={onboardingGuides} />
      </TabWrapper>
    )
  }

  switch (parsedActiveTab.type) {
    case 'block':
      return (
        <TabWrapper>
          <BreadcrumbDashboard
            type="editor"
            activeBlockId={parsedActiveTab.blockId}
          />
          <DocumentBlock
            key={parsedActiveTab.blockId}
            blockId={parsedActiveTab.blockId}
          />
        </TabWrapper>
      )
    case 'block:diff':
      return (
        <TabWrapper>
          <WorkingBlockDiff
            key={parsedActiveTab.blockId}
            blockId={parsedActiveTab.blockId}
          />
        </TabWrapper>
      )
    case 'commit':
      return (
        <TabWrapper>
          <CommitContent commitId={parsedActiveTab.commitId} />
        </TabWrapper>
      )
    case 'timeline':
      return (
        <TabWrapper>
          <DefaultTab onboardingGuides={onboardingGuides} />
        </TabWrapper>
      )
    case 'merge': {
      return (
        <TabWrapper>
          {parsedActiveTab.blockId && (
            <BranchChangesTabContent
              crId={parsedActiveTab.crId}
              activeBlockId={parsedActiveTab.blockId}
            />
          )}
        </TabWrapper>
      )
    }
    case 'changelog': {
      return <ChangelogEditorEntry entryId={parsedActiveTab.entryId} />
    }
    case 'branches': {
      return (
        <TabWrapper>
          <BranchManager />
        </TabWrapper>
      )
    }
    case 'dev': {
      return (
        <TabWrapper>
          <DevTabContent parsedTab={parsedActiveTab} />
        </TabWrapper>
      )
    }
    case 'settings': {
      return (
        <TabWrapper>
          <SettingsTabContent parsedTab={parsedActiveTab} />
        </TabWrapper>
      )
    }
    case 'agents': {
      return (
        <TabWrapper>
          <AgentsTabContent parsedTab={parsedActiveTab} />
        </TabWrapper>
      )
    }
    case 'site': {
      return (
        <TabWrapper>
          <SiteTabContent parsedTab={parsedActiveTab} />
        </TabWrapper>
      )
    }
    case 'suggestion': {
      return (
        <TabWrapper>
          <SuggestionContent branchId={parsedActiveTab.branchId} />
        </TabWrapper>
      )
    }
    default:
      throw new Error(
        `Unknown tab type for tab: ${JSON.stringify(parsedActiveTab)}`
      )
  }
}

const DefaultTab = ({
  onboardingGuides,
}: {
  onboardingGuides?: React.ReactNode
}) => {
  const isVisitor = useIsExternalVisitor()
  const isGuest = useIsGuest()
  const isGuestInHisBranch = useIsGuestInHisBranch()
  const branch = useSelectedBranch().branch

  if (
    isVisitor &&
    !isGuest &&
    !isGuestInHisBranch &&
    (!branch || branch?.isDefault)
  ) {
    return <READMETabContent />
  }

  return <Timeline onboardingGuides={onboardingGuides} />
}
